import { useState, useEffect } from "react";
import { ITask } from "@/interfaces/dashboard.interface";
import {
  Dialog,
  DialogContent,
  DialogClose,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import AvatarGroup from "./avatar-group";
import { X, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ViewTaskDialogProps {
  open: boolean;
  task: ITask | null;
  onClose: () => void;
  onUpdateTask?: (updatedTask: ITask) => void;
}

export default function ViewTaskDialog({
  open,
  task,
  onClose,
  onUpdateTask,
}: ViewTaskDialogProps) {
  const [checklist, setChecklist] = useState(task?.checklist || []);
  const [newChecklistItem, setNewChecklistItem] = useState("");

  // Sync checklist when task changes
  useEffect(() => {
    if (task?.checklist) {
      setChecklist(task.checklist);
    }
  }, [task]);

  if (!task) return null;

  // Handle checkbox toggle
  const handleChecklistToggle = (checklistId: string) => {
    const updated = checklist.map((item) =>
      item.id === checklistId ? { ...item, checked: !item.checked } : item
    );
    setChecklist(updated);

    // Update parent
    if (onUpdateTask) {
      onUpdateTask({ ...task, checklist: updated });
    }
  };

  // Handle add new checklist item
  const handleAddChecklistItem = () => {
    if (!newChecklistItem.trim()) return;

    const newItem = {
      id: `check-${task.id}-${Date.now()}`,
      text: newChecklistItem,
      checked: false,
    };

    const updated = [...checklist, newItem];
    setChecklist(updated);
    setNewChecklistItem("");

    // Update parent
    if (onUpdateTask) {
      onUpdateTask({ ...task, checklist: updated });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[932px] h-[792px] bg-[#15203C] border-none p-[40px] overflow-y-auto">
        {/* Close Button */}
        <DialogClose className="absolute right-[40px] top-[47px] w-[24px] h-[24px] text-white hover:text-gray-300 transition-colors">
          <X className="w-full h-full" />
        </DialogClose>

        {/* Header Section - 852×92 */}
        <div className="w-[852px] h-[92px] flex flex-col gap-[12px]">
          {/* Title */}
          <h2 className="font-['Plus_Jakarta_Sans'] font-bold text-[40px] leading-[40px] text-white">
            {task.taskName}
          </h2>

          {/* Brief Description */}
          <p className="font-['Plus_Jakarta_Sans'] font-normal text-[16px] leading-[20px] text-[#D1D1D1]">
            Breve descrição Lorem ipsum dolor sit amet consectetur. Urna augue gravida id pellentesque donec dolor velit magna.
          </p>
        </div>

        {/* Info Section - 532×73 */}
        <div className="mt-[40px] flex gap-[43px] items-start">
          {/* Tags - 384×73 */}
          <div className="w-[384px] flex flex-col gap-[14px]">
            <p className="font-['Plus_Jakarta_Sans'] font-normal text-[16px] leading-[20px] text-white">
              Etiquetas
            </p>
            <div className="flex gap-[14px]">
              {task.tags?.slice(0, 2).map((tag, index) => (
                <div
                  key={index}
                  className="h-[39px] px-[21px] rounded-[20px] flex items-center justify-center"
                  style={{ backgroundColor: tag.color }}
                >
                  <span className="font-['Plus_Jakarta_Sans'] font-medium text-[16px] text-white whitespace-nowrap">
                    {tag.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Responsáveis - 126×66 */}
          <div className="w-[126px] flex flex-col gap-[19px]">
            <p className="font-['Plus_Jakarta_Sans'] font-normal text-[16px] leading-[20px] text-white">
              Responsáveis
            </p>
            {task.assignees && task.assignees.length > 0 && (
              <AvatarGroup assignees={task.assignees} maxAvatars={4} />
            )}
          </div>

          {/* Data - 105×64 */}
          <div className="w-[105px] flex flex-col gap-[24px]">
            <p className="font-['Plus_Jakarta_Sans'] font-normal text-[16px] leading-[20px] text-white">
              Data
            </p>
            <p className="font-['Plus_Jakarta_Sans'] font-normal text-[16px] leading-[20px] text-white">
              {task.date}
            </p>
          </div>
        </div>

        {/* Description Section - 852×163 */}
        <div className="mt-[40px] w-[852px] flex flex-col gap-[17px]">
          <p className="font-['Plus_Jakarta_Sans'] font-normal text-[16px] leading-[20px] text-white">
            Descrição
          </p>
          <div className="w-[852px] h-[126px] bg-[#0C1427] rounded-[10px] p-[20px]">
            <p className="font-['Plus_Jakarta_Sans'] font-normal text-[14px] leading-[21.5px] text-[#D1D1D1]">
              {task.description || "Sem descrição"}
            </p>
          </div>
        </div>

        {/* Checklist Section - 852×212 */}
        <div className="mt-[40px] w-[852px] flex flex-col gap-[17px]">
          <p className="font-['Plus_Jakarta_Sans'] font-normal text-[16px] leading-[20px] text-white">
            Checklist
          </p>

          {/* Checklist Items */}
          <div className="flex flex-col gap-[17px]">
            {checklist.map((item) => (
              <div key={item.id} className="flex items-center gap-[14px]">
                <Checkbox
                  checked={item.checked}
                  onCheckedChange={() => handleChecklistToggle(item.id)}
                  className="w-[24px] h-[24px]"
                />
                <span className="font-['Plus_Jakarta_Sans'] font-normal text-[14px] leading-[20px] text-white">
                  {item.text}
                </span>
              </div>
            ))}

            {/* Add New Item Input */}
            <div className="flex items-center gap-[14px] mt-[6px]">
              <input
                type="text"
                value={newChecklistItem}
                onChange={(e) => setNewChecklistItem(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleAddChecklistItem();
                  }
                }}
                placeholder="Nova tarefa..."
                className="flex-1 bg-transparent border-none outline-none text-white text-[14px] placeholder:text-gray-500"
              />
            </div>
          </div>

          {/* Add Button */}
          <Button
            onClick={handleAddChecklistItem}
            className="bg-[#7846FF] hover:bg-[#6635E6] text-white h-[50px] w-[152px] rounded-[10px] flex items-center gap-[10px] mt-[12px]"
          >
            <Plus className="w-[24px] h-[24px]" />
            <span className="font-['Plus_Jakarta_Sans'] font-bold text-[14px]">
              Add novo item
            </span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
